import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "@/components/proof/PageShell";
import Countdown from "@/components/proof/Countdown";
import {
    BlinkingDot,
    MonoLabel,
    FieldButton,
} from "@/components/proof/primitives";
import { easing } from "@/lib/animations";
import { DROPS, getDropBySlug, isLaunched } from "@/data/drops";
import { PRODUCTS, formatPrice } from "@/data/products";

const STORAGE_PREFIX = "proof:drop-access:";

const dropAccessKey = (slug) => `${STORAGE_PREFIX}${slug}`;

const hasAccess = (drop) => {
    if (!drop) return false;
    if (!drop.passcode) return true;
    if (isLaunched(drop)) return true;
    try {
        return window.localStorage.getItem(dropAccessKey(drop.slug)) === "1";
    } catch (e) {
        return false;
    }
};

const grantAccess = (drop) => {
    try {
        window.localStorage.setItem(dropAccessKey(drop.slug), "1");
    } catch (e) {
        /* ignore */
    }
};

/* ---------- Passcode gate — quiet brutalist lock screen ---------- */
const PasscodeGate = ({ drop, onUnlock }) => {
    const [code, setCode] = useState("");
    const [err, setErr] = useState(null);
    const [shake, setShake] = useState(0);

    const onSubmit = (e) => {
        e.preventDefault();
        if (code.trim().toUpperCase() === drop.passcode.toUpperCase()) {
            grantAccess(drop);
            onUnlock();
        } else {
            setErr("Wrong signal.");
            setShake((s) => s + 1);
            setTimeout(() => setErr(null), 1800);
        }
    };

    return (
        <PageShell
            sectionNumber={drop.n}
            eyebrow={drop.eyebrow}
            title={drop.title}
            crumb="Pre-launch"
            data-testid={`page-drop-gate-${drop.slug}`}
        >
            <motion.div
                key={shake}
                initial={{ x: 0 }}
                animate={shake ? { x: [-6, 6, -4, 4, 0] } : {}}
                transition={{ duration: 0.4 }}
                className="proof-grid-equal"
                data-testid="drop-gate"
            >
                {/* LEFT — passcode form */}
                <form
                    onSubmit={onSubmit}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 22,
                    }}
                    data-testid="drop-gate-form"
                >
                    <p
                        style={{
                            margin: 0,
                            fontFamily: "var(--font-serif)",
                            fontStyle: "italic",
                            fontSize: "clamp(18px, 1.5vw, 22px)",
                            lineHeight: 1.55,
                            color: "var(--on-ink-soft)",
                            maxWidth: "44ch",
                        }}
                    >
                        {drop.tagline} Access is invitation-only until the
                        signal opens.
                    </p>

                    <label
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                        }}
                    >
                        <MonoLabel tone="mute">Passcode</MonoLabel>
                        <input
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="••••••••"
                            autoComplete="off"
                            spellCheck={false}
                            data-testid="drop-gate-input"
                            style={{
                                background: "transparent",
                                border: "1px solid var(--on-ink-line-strong, rgba(255,255,255,0.45))",
                                color: "var(--on-ink)",
                                fontFamily: "var(--font-mono)",
                                fontSize: 18,
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                padding: "14px 16px",
                                outline: "none",
                            }}
                        />
                    </label>

                    {err && (
                        <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            data-testid="drop-gate-error"
                            style={{
                                color: "var(--signal)",
                                fontFamily: "var(--font-mono)",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                            }}
                        >
                            {err}
                        </motion.div>
                    )}

                    <FieldButton
                        variant="solid"
                        type="submit"
                        data-testid="drop-gate-submit"
                        style={{ alignSelf: "flex-start" }}
                    >
                        Unlock Field
                    </FieldButton>

                    <MonoLabel
                        tone="faint"
                        style={{ marginTop: 12, maxWidth: "50ch" }}
                    >
                        No passcode? The drop opens to everyone on launch — sign
                        the&nbsp;
                        <Link
                            to="/contact"
                            style={{
                                color: "var(--on-ink-soft)",
                                textDecoration: "underline",
                            }}
                        >
                            studio newsletter
                        </Link>
                        &nbsp;for the signal.
                    </MonoLabel>
                </form>

                {/* RIGHT — countdown + teaser */}
                <aside
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 28,
                        padding: "26px 24px",
                        border: "1px solid var(--on-ink-line)",
                        background: "var(--on-ink-surface, rgba(255,255,255,0.04))",
                    }}
                >
                    <MonoLabel
                        tone="signal"
                        style={{ display: "inline-flex", alignItems: "center", gap: 12 }}
                    >
                        <BlinkingDot />
                        Signal opens in
                    </MonoLabel>
                    <Countdown
                        target={drop.launchAt}
                        size="default"
                        data-testid="drop-gate-countdown"
                    />
                    <div
                        style={{
                            aspectRatio: "4 / 3",
                            background: drop.bgTone || "#1a1a1a",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginTop: 8,
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        <img
                            src={drop.heroPoster}
                            alt={drop.title}
                            draggable={false}
                            style={{
                                width: "70%",
                                height: "70%",
                                objectFit: "contain",
                                mixBlendMode: "multiply",
                            }}
                        />
                        <span
                            style={{
                                position: "absolute",
                                top: 14,
                                left: 14,
                                fontFamily: "var(--font-mono)",
                                fontSize: 10,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                color: "rgba(12,12,12,0.55)",
                            }}
                        >
                            Teaser
                        </span>
                    </div>
                </aside>
            </motion.div>
        </PageShell>
    );
};

/* ---------- Open Drop Hero — large brand statement ---------- */
const DropHero = ({ drop, launched }) => (
    <section
        data-testid="drop-hero"
        style={{
            position: "relative",
            background: "var(--ink)",
            color: "var(--on-ink)",
            padding:
                "clamp(120px, 14vw, 200px) clamp(24px, 5vw, 80px) clamp(60px, 8vw, 100px)",
        }}
        className="proof-page-shell"
    >
        {/* Meta row */}
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 18,
                marginBottom: "clamp(36px, 5vw, 72px)",
            }}
        >
            <MonoLabel
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 12,
                }}
            >
                <BlinkingDot />
                Drop [{drop.n}] · {launched ? "Live" : "Pre-launch"}
            </MonoLabel>
            <MonoLabel
                tone="mute"
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 12,
                }}
            >
                <Link to="/" style={{ color: "inherit", textDecoration: "none" }}>
                    Proof
                </Link>
                <span aria-hidden="true">/</span>
                <span style={{ color: "var(--on-ink-soft)" }}>
                    Drop · {drop.title}
                </span>
            </MonoLabel>
        </div>

        {/* Eyebrow + title */}
        <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.95, delay: 0.18, ease: easing.primary }}
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 14,
                marginBottom: "clamp(28px, 4vw, 56px)",
            }}
        >
            <MonoLabel tone="mute">{drop.eyebrow}</MonoLabel>
            <h1
                data-testid="drop-title"
                style={{
                    margin: 0,
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: "clamp(48px, 9vw, 168px)",
                    letterSpacing: "-0.045em",
                    lineHeight: 0.88,
                    textTransform: "uppercase",
                }}
            >
                {drop.title}
            </h1>
        </motion.div>

        {/* Tagline + manifesto */}
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.95, delay: 0.36, ease: easing.primary }}
            style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                gap: "clamp(28px, 4vw, 60px)",
                alignItems: "start",
            }}
            className="proof-grid-main"
        >
            <p
                style={{
                    margin: 0,
                    fontFamily: "var(--font-serif)",
                    fontStyle: "italic",
                    fontSize: "clamp(22px, 2.4vw, 36px)",
                    lineHeight: 1.4,
                    color: "var(--on-ink)",
                    maxWidth: "26ch",
                }}
            >
                {drop.tagline}
            </p>
            <p
                style={{
                    margin: 0,
                    fontFamily: "var(--font-body)",
                    fontSize: "clamp(15px, 1.1vw, 17px)",
                    lineHeight: 1.65,
                    color: "var(--on-ink-soft)",
                    maxWidth: "50ch",
                }}
            >
                {drop.manifesto}
            </p>
        </motion.div>
    </section>
);

/* ---------- Lookbook strip — slow editorial image grid ---------- */
const Lookbook = ({ drop }) => (
    <section
        data-testid="drop-lookbook"
        style={{
            background: "var(--ink)",
            color: "var(--on-ink)",
            padding: "clamp(40px, 6vw, 80px) clamp(24px, 5vw, 80px)",
        }}
    >
        <div
            style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                paddingBottom: 14,
                borderBottom: "1px solid var(--on-ink-line)",
                marginBottom: 36,
            }}
        >
            <h2
                style={{
                    margin: 0,
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: "clamp(28px, 3.2vw, 48px)",
                    letterSpacing: "-0.025em",
                    textTransform: "uppercase",
                }}
            >
                Lookbook
            </h2>
            <MonoLabel tone="mute">
                {String(drop.gallery.length).padStart(2, "0")} · Frames
            </MonoLabel>
        </div>
        <div
            style={{
                display: "grid",
                gridTemplateColumns:
                    "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "clamp(20px, 2vw, 36px)",
            }}
        >
            {drop.gallery.map((src, i) => (
                <motion.div
                    key={src + i}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{
                        duration: 0.9,
                        delay: 0.05 * i,
                        ease: easing.primary,
                    }}
                    data-testid={`drop-look-${i}`}
                    style={{
                        aspectRatio: i % 3 === 0 ? "3 / 4" : "1 / 1",
                        background: drop.bgTone || "#1a1a1a",
                        position: "relative",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <img
                        src={src}
                        alt={`${drop.title} ${String(i + 1).padStart(2, "0")}`}
                        draggable={false}
                        style={{
                            width: "78%",
                            height: "78%",
                            objectFit: "contain",
                            mixBlendMode: "multiply",
                        }}
                    />
                    <span
                        style={{
                            position: "absolute",
                            top: 14,
                            left: 14,
                            fontFamily: "var(--font-mono)",
                            fontSize: 10,
                            letterSpacing: "0.24em",
                            textTransform: "uppercase",
                            color: "rgba(12,12,12,0.55)",
                        }}
                    >
                        Frame {String(i + 1).padStart(2, "0")}
                    </span>
                </motion.div>
            ))}
        </div>
    </section>
);

/* ---------- Artifacts grid — show the actual products tied to the drop ---------- */
const DropArtifacts = ({ drop, products }) => {
    if (products.length === 0) return null;
    return (
        <section
            data-testid="drop-artifacts"
            style={{
                background: "var(--ink)",
                color: "var(--on-ink)",
                padding:
                    "clamp(40px, 6vw, 80px) clamp(24px, 5vw, 80px) clamp(80px, 10vw, 160px)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    paddingBottom: 14,
                    borderBottom: "1px solid var(--on-ink-line)",
                    marginBottom: 36,
                }}
            >
                <h2
                    style={{
                        margin: 0,
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: "clamp(28px, 3.2vw, 48px)",
                        letterSpacing: "-0.025em",
                        textTransform: "uppercase",
                    }}
                >
                    Artifacts
                </h2>
                <MonoLabel tone="mute">
                    {String(products.length).padStart(2, "0")} · In rotation
                </MonoLabel>
            </div>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns:
                        "repeat(auto-fill, minmax(240px, 1fr))",
                    gap: "clamp(20px, 2vw, 36px)",
                }}
            >
                {products.map((p, i) => (
                    <motion.div
                        key={p.n}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.25 }}
                        transition={{
                            duration: 0.8,
                            delay: 0.05 * (i % 4),
                            ease: easing.primary,
                        }}
                        data-testid={`drop-artifact-${p.slug}`}
                    >
                        <Link
                            to={`/product/${p.slug}`}
                            style={{
                                color: "inherit",
                                textDecoration: "none",
                                display: "block",
                            }}
                        >
                            <div
                                style={{
                                    aspectRatio: "1 / 1",
                                    background: p.bgTone,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <motion.img
                                    layoutId={`product-cover-${p.slug}`}
                                    src={p.cover}
                                    alt={p.name}
                                    style={{
                                        width: "82%",
                                        height: "82%",
                                        objectFit: "contain",
                                        mixBlendMode: "multiply",
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    paddingTop: 12,
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <span
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontWeight: 500,
                                        fontSize: 15,
                                        letterSpacing: "0.04em",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {p.name}
                                </span>
                                <span
                                    style={{
                                        fontFamily: "var(--font-mono)",
                                        fontSize: 12,
                                        color: "var(--on-ink-mute)",
                                    }}
                                >
                                    {formatPrice(p.priceEUR)}
                                </span>
                            </div>
                        </Link>
                    </motion.div>
                ))}
            </div>
        </section>
    );
};

export default function Drop() {
    const { slug } = useParams();
    const drop = getDropBySlug(slug);
    const [unlocked, setUnlocked] = useState(() => hasAccess(drop));
    const launched = drop ? isLaunched(drop) : false;
    const products = useMemo(
        () => (drop ? PRODUCTS.filter((p) => p.drop === drop.productDropTag) : []),
        [drop],
    );

    useEffect(() => {
        setUnlocked(hasAccess(drop));
    }, [drop]);

    if (!drop) {
        return (
            <PageShell
                sectionNumber="--"
                eyebrow="Off the field"
                title="Drop not found"
                crumb="Drop"
                data-testid="page-drop-notfound"
            >
                <p
                    style={{
                        margin: 0,
                        fontFamily: "var(--font-serif)",
                        fontStyle: "italic",
                        fontSize: 19,
                        color: "var(--on-ink-mute)",
                        maxWidth: "44ch",
                        marginBottom: 28,
                    }}
                >
                    That drop hasn't been catalogued. Browse the open Field
                    instead.
                </p>
                <FieldButton to="/shop" data-testid="drop-notfound-shop">
                    Enter Lookbook
                </FieldButton>
                <div
                    style={{
                        marginTop: 36,
                        display: "flex",
                        gap: 12,
                        flexWrap: "wrap",
                    }}
                >
                    {DROPS.map((d) => (
                        <FieldButton
                            key={d.slug}
                            to={`/drop/${d.slug}`}
                            size="sm"
                            data-testid={`drop-link-${d.slug}`}
                        >
                            {d.title}
                        </FieldButton>
                    ))}
                </div>
            </PageShell>
        );
    }

    const launchedOk = launched;
    const productList = products;

    // Locked pre-launch: render the gate.
    if (drop.passcode && !launchedOk && !unlocked) {
        return <PasscodeGate drop={drop} onUnlock={() => setUnlocked(true)} />;
    }

    return (
        <main data-testid={`page-drop-${drop.slug}`}>
            <DropHero drop={drop} launched={launchedOk} />

            {!launchedOk && (
                <section
                    data-testid="drop-countdown-section"
                    style={{
                        background: "var(--ink)",
                        color: "var(--on-ink)",
                        padding:
                            "clamp(40px, 5vw, 70px) clamp(24px, 5vw, 80px)",
                        borderTop: "1px solid var(--on-ink-line)",
                        borderBottom: "1px solid var(--on-ink-line)",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 28,
                            alignItems: "center",
                            textAlign: "center",
                        }}
                    >
                        <MonoLabel
                            tone="signal"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 12,
                            }}
                        >
                            <BlinkingDot />
                            Signal opens in
                        </MonoLabel>
                        <Countdown
                            target={drop.launchAt}
                            size="lg"
                            data-testid="drop-countdown"
                        />
                        <MonoLabel tone="faint" style={{ maxWidth: "44ch" }}>
                            All artifacts ship from the Warsaw atelier within
                            five business days of the signal.
                        </MonoLabel>
                    </div>
                </section>
            )}

            <Lookbook drop={drop} />
            <DropArtifacts drop={drop} products={productList} />

            {/* Footer CTA — go to all drops or back to shop */}
            <section
                style={{
                    background: "var(--ink)",
                    color: "var(--on-ink)",
                    padding:
                        "clamp(40px, 5vw, 80px) clamp(24px, 5vw, 80px) clamp(100px, 12vw, 160px)",
                    borderTop: "1px solid var(--on-ink-line)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 22,
                }}
            >
                <MonoLabel tone="mute">— Other drops in rotation</MonoLabel>
                <div
                    style={{
                        display: "flex",
                        gap: 14,
                        flexWrap: "wrap",
                    }}
                >
                    {DROPS.filter((d) => d.slug !== drop.slug).map((d) => (
                        <FieldButton
                            key={d.slug}
                            to={`/drop/${d.slug}`}
                            data-testid={`drop-other-${d.slug}`}
                        >
                            {d.title}
                            <svg
                                width="22"
                                height="9"
                                viewBox="0 0 22 9"
                                fill="none"
                                aria-hidden="true"
                            >
                                <line x1="0" y1="4.5" x2="20" y2="4.5" stroke="currentColor" />
                                <path d="M16 1L20 4.5L16 8" stroke="currentColor" fill="none" />
                            </svg>
                        </FieldButton>
                    ))}
                </div>
            </section>
        </main>
    );
}
